/*!

=========================================================
* Argon Dashboard PRO React - v1.2.5
=========================================================

* Product Page: https://www.easyhub.ai/product/argon-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.easyhub.ai)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUserCircle } from "react-icons/fa";
import { FiGlobe } from "react-icons/fi";
import { LANGUAGES } from "../../i18n";
import "./AdminNavbar.scss";

type AdminNavbarProps = {
  theme: string,
  sidenavOpen: boolean,
  role: string,
  toggleSidenav: (e: any) => void,
  fullName: string,
  logout: () => void,
  cleanup: () => void,
  partnerLogo?: string | null,
  partnerName?: string | null
}

function AdminNavbar({ theme, partnerLogo, partnerName }: AdminNavbarProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageMenu]);

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLanguageMenu(false);
  };

  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  return (
    <nav className="admin-navbar admin-navbar--transparent">
      <div className="admin-navbar__container">
        {/* Partner Logo */}
        <div
          className="admin-navbar__logo"
          onClick={() => navigate('/admin/home')}
        >
          {partnerLogo ? (
            <img
              alt={partnerName || "Partner"}
              src={partnerLogo}
            />
          ) : partnerName ? (
            <span className="admin-navbar__partner-name">{partnerName}</span>
          ) : (
            <img
              alt="EasyHub"
              src="https://steasyhubprodus.blob.core.windows.net/public/logo-white.png"
            />
          )}
        </div>

        {/* Right Actions */}
        <div className="admin-navbar__actions">
          {/* Language Selector with Flag */}
          <div className="admin-navbar__language" ref={languageMenuRef}>
            <button
              className="admin-navbar__action-btn admin-navbar__action-btn--lang"
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              aria-label="Change Language"
            >
              <FiGlobe size={16} className="admin-navbar__globe-icon" />
              <span className="admin-navbar__current-flag">{currentLanguage.flag}</span>
            </button>
            {showLanguageMenu && (
              <div className="admin-navbar__language-dropdown">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    className={`admin-navbar__language-option ${
                      currentLanguage.code === lang.code ? 'admin-navbar__language-option--active' : ''
                    }`}
                    onClick={() => changeLanguage(lang.code)}
                  >
                    <span className="admin-navbar__language-flag">{lang.flag}</span>
                    <span className="admin-navbar__language-name">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="admin-navbar__action-btn"
            onClick={() => navigate('/admin/profile')}
            aria-label="Profile"
          >
            <FaUserCircle size={24} />
          </button>
        </div>
      </div>
    </nav>
  );
}

AdminNavbar.defaultProps = {
  toggleSidenav: () => { },
  sidenavOpen: false,
  theme: "dark",
};

export default AdminNavbar;
